var React = require('react');
var ReactDOM = require('react-dom');
var { createElement: h, useState, useEffect, useRef, useCallback } = React;
var { request } = require('../lib/apiClient');
require('./FeedbackWidget.css');

// The three categories the backend accepts (see feedbackService.js). Keep the
// keys in lockstep with CATEGORIES there.
var CATEGORIES = [
  { key: 'bug', label: 'Bug' },
  { key: 'idea', label: 'Idea' },
  { key: 'other', label: 'Other' }
];

// The launcher shakes for the whole time the "Any feedback?" balloon is
// visible — they start and stop together.
var BALLOON_MS = 2800;
// Ambient shake also fires on a random 5–20s interval (on top of the shake
// that fires when a workflow run ends).
var SHAKE_GAP_MIN = 5000;
var SHAKE_GAP_MAX = 20000;

function randomGap() {
  return SHAKE_GAP_MIN + Math.random() * (SHAKE_GAP_MAX - SHAKE_GAP_MIN);
}

// Image-attachment limits. Kept in sync with feedbackService.js on the server.
// The total cap keeps the base64 JSON body under the server's 15 MB limit
// (raw 9 MB → ~12 MB base64).
var MAX_IMAGES = 5;
var MAX_IMAGE_BYTES = 3 * 1024 * 1024; // 3 MB per image
var MAX_TOTAL_BYTES = 9 * 1024 * 1024; // 9 MB across all images

// Read one File into a { name, dataUrl } pair. Resolves null on read error so
// one bad file never blocks the rest.
function readImage(file) {
  return new Promise(function(resolve) {
    var reader = new FileReader();
    reader.onload = function() { resolve({ name: file.name, dataUrl: String(reader.result || ''), bytes: file.size }); };
    reader.onerror = function() { resolve(null); };
    reader.readAsDataURL(file);
  });
}

// Small chat-bubble icon for the launcher button.
function FeedbackIcon() {
  return h('svg', { width: 17, height: 17, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    h('path', { d: 'M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z' })
  );
}

// Plus icon for the "add image" tile.
function PlusIcon() {
  return h('svg', { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' },
    h('line', { x1: 12, y1: 5, x2: 12, y2: 19 }),
    h('line', { x1: 5, y1: 12, x2: 19, y2: 12 })
  );
}

// In-app product feedback: a launcher button that shakes (with an "Any
// feedback?" balloon) each time a workflow run ends, and a popup (category +
// message + up to 5 images) that emails the team.
// Props: { isExecutionRunning } — the run-ended edge drives the shake.
function FeedbackWidget(props) {
  var isExecutionRunning = props && props.isExecutionRunning;
  var [open, setOpen] = useState(false);
  var [shaking, setShaking] = useState(false);
  var [balloon, setBalloon] = useState(false);
  var [category, setCategory] = useState('idea');
  var [email, setEmail] = useState('');
  var [message, setMessage] = useState('');
  var [images, setImages] = useState([]); // [{ name, dataUrl }]
  var [status, setStatus] = useState('idle'); // idle | sending | sent | error
  var [errorMsg, setErrorMsg] = useState('');
  var textareaRef = useRef(null);
  var fileInputRef = useRef(null);
  // The signed-in user's account email, fetched once — used to prefill the
  // email field (and to reset it to on close).
  var accountEmailRef = useRef('');

  // Prefill the email field from the account on mount.
  useEffect(function() {
    request('/auth/me').then(function(res) {
      var em = res && res.user && res.user.email ? String(res.user.email) : '';
      if (em) {
        accountEmailRef.current = em;
        setEmail(function(cur) { return cur || em; });
      }
    }).catch(function() {});
  }, []);

  // Fire one shake + "Any feedback?" balloon. Clears any in-flight timers first
  // so back-to-back triggers don't stack.
  var shakeTimersRef = useRef({});
  var fireShake = useCallback(function() {
    var t = shakeTimersRef.current;
    clearTimeout(t.shake);
    setShaking(true);
    setBalloon(true);
    t.shake = setTimeout(function() { setShaking(false); setBalloon(false); }, BALLOON_MS);
  }, []);

  // Nudge for feedback whenever a workflow run ENDS — i.e. isExecutionRunning
  // goes true → false, which covers both a natural finish and a manual Stop.
  // Never nudges while the popup is already open.
  var wasRunningRef = useRef(false);
  useEffect(function() {
    var running = !!isExecutionRunning;
    if (wasRunningRef.current && !running && !open) fireShake();
    wasRunningRef.current = running;
  }, [isExecutionRunning, open, fireShake]);

  // Ambient nudge — shake every 5–20s while the popup is closed, so the button
  // keeps inviting feedback even between workflow runs.
  useEffect(function() {
    if (open) return undefined;
    var timer = null;
    function loop() {
      timer = setTimeout(function() { fireShake(); loop(); }, randomGap());
    }
    loop();
    return function() { clearTimeout(timer); };
  }, [open, fireShake]);

  // Stop shaking / clear the balloon when the popup opens; clean up on unmount.
  useEffect(function() {
    if (open) { setShaking(false); setBalloon(false); }
  }, [open]);
  useEffect(function() {
    return function() {
      clearTimeout(shakeTimersRef.current.shake);
    };
  }, []);

  // Focus the textarea when the popup opens so the user can type immediately.
  useEffect(function() {
    if (open && textareaRef.current) textareaRef.current.focus();
  }, [open]);

  var closePopup = useCallback(function() {
    setOpen(false);
    setStatus('idle');
    setMessage('');
    setEmail(accountEmailRef.current || '');
    setCategory('idea');
    setImages([]);
    setErrorMsg('');
  }, []);

  // Shared image intake — used by the file picker AND clipboard paste. Caps at
  // MAX_IMAGES total, images only, size-limited.
  var addFiles = useCallback(function(fileList) {
    var picked = Array.prototype.slice.call(fileList || []);
    if (!picked.length) return;
    setErrorMsg('');
    var room = MAX_IMAGES - images.length;
    if (room <= 0) { setErrorMsg('You can attach up to ' + MAX_IMAGES + ' images.'); return; }
    var running = images.reduce(function(s, i) { return s + (i.bytes || 0); }, 0);
    var tooBig = false;
    var overTotal = false;
    var valid = [];
    picked.forEach(function(f) {
      if (valid.length >= room) return;
      if (!f || f.type.indexOf('image/') !== 0) return;
      if (f.size > MAX_IMAGE_BYTES) { tooBig = true; return; }
      if (running + f.size > MAX_TOTAL_BYTES) { overTotal = true; return; }
      running += f.size;
      valid.push(f);
    });
    if (tooBig) setErrorMsg('Each image must be under 3 MB.');
    else if (overTotal) setErrorMsg('Total images must stay under 9 MB.');
    if (!valid.length) return;
    Promise.all(valid.map(readImage)).then(function(read) {
      var ok = read.filter(Boolean);
      if (ok.length) setImages(function(c) { return c.concat(ok).slice(0, MAX_IMAGES); });
    });
  }, [images]);

  var onPickFiles = useCallback(function(e) {
    var files = e.target && e.target.files;
    if (e.target) e.target.value = ''; // allow re-picking the same file later
    addFiles(files);
  }, [addFiles]);

  // Paste an image straight into the popup (Ctrl/Cmd+V). Pulls image blobs out
  // of the clipboard items and feeds them through the same intake path.
  var onPaste = useCallback(function(e) {
    var items = (e.clipboardData && e.clipboardData.items) || [];
    var files = [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].kind === 'file' && items[i].type.indexOf('image/') === 0) {
        var f = items[i].getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length) { e.preventDefault(); addFiles(files); }
  }, [addFiles]);

  var removeImage = useCallback(function(idx) {
    setImages(function(cur) { return cur.filter(function(_, i) { return i !== idx; }); });
  }, []);

  var submit = useCallback(function(e) {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    var trimmed = message.trim();
    if (!trimmed || status === 'sending') return;
    setStatus('sending');
    setErrorMsg('');
    request('/feedback', { method: 'POST', body: { category: category, email: email.trim(), message: trimmed, images: images } })
      .then(function(res) {
        // The endpoint returns 201 even if the email couldn't be delivered
        // (best-effort mailer). Only claim success when it actually went out.
        if (res && res.delivered === false) {
          setErrorMsg('Saved, but the email didn’t go out — please tell the team directly.');
          setStatus('error');
          return;
        }
        setStatus('sent');
        setTimeout(closePopup, 1400);
      })
      .catch(function() { setStatus('error'); });
  }, [message, category, images, status, closePopup]);

  var canAddMore = images.length < MAX_IMAGES;

  return h('div', { className: 'feedback-widget' },
    // Portaled to <body> so it escapes the footer's stacking context and can
    // sit above the copilot popup (z-index 1001) and toasts.
    open ? ReactDOM.createPortal(h('div', {
      className: 'feedback-popup',
      // Keep clicks/scroll/keys inside the popup — the canvas listens for these
      // and would otherwise pan/zoom behind the popup.
      onClick: function(e) { e.stopPropagation(); },
      onMouseDown: function(e) { e.stopPropagation(); },
      onWheel: function(e) { e.stopPropagation(); },
      onPaste: onPaste
    },
      h('div', { className: 'feedback-popup-head' },
        h('span', { className: 'feedback-popup-title' }, 'Send feedback'),
        h('button', {
          className: 'feedback-popup-close', type: 'button', title: 'Close',
          onClick: function(e) { e.preventDefault(); e.stopPropagation(); closePopup(); }
        }, '×')
      ),
      status === 'sent'
        ? h('div', { className: 'feedback-popup-sent' },
            h('div', { className: 'feedback-popup-sent-check' }, '✓'),
            h('div', null, 'Thanks — we got it!')
          )
        : h('div', { className: 'feedback-popup-body' },
            h('label', { className: 'feedback-field-label' }, 'Type'),
            h('div', { className: 'feedback-category-row' },
              CATEGORIES.map(function(c) {
                return h('button', {
                  key: c.key,
                  type: 'button',
                  className: 'feedback-category-btn' + (category === c.key ? ' feedback-category-btn-active' : ''),
                  onClick: function(e) { e.preventDefault(); e.stopPropagation(); setCategory(c.key); }
                }, c.label);
              })
            ),
            h('label', { className: 'feedback-field-label' }, 'Your email'),
            h('input', {
              className: 'feedback-email-input',
              type: 'email',
              value: email,
              placeholder: 'you@example.com — so we can reply',
              onChange: function(e) { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }
            }),
            h('label', { className: 'feedback-field-label' }, 'Your feedback'),
            h('textarea', {
              ref: textareaRef,
              className: 'feedback-textarea',
              value: message,
              maxLength: 4000,
              placeholder: 'What works, what doesn’t, what would you love to see?',
              onChange: function(e) { setMessage(e.target.value); if (status === 'error') setStatus('idle'); }
            }),
            // Image attachments
            h('div', { className: 'feedback-images' },
              h('label', { className: 'feedback-field-label' }, 'Screenshots (optional)'),
              h('div', { className: 'feedback-image-grid' },
                images.map(function(img, idx) {
                  return h('div', { key: idx, className: 'feedback-image-thumb' },
                    h('img', { src: img.dataUrl, alt: img.name || 'attachment' }),
                    h('button', {
                      type: 'button',
                      className: 'feedback-image-remove',
                      title: 'Remove',
                      onClick: function(e) { e.preventDefault(); e.stopPropagation(); removeImage(idx); }
                    }, '×')
                  );
                }),
                canAddMore ? h('button', {
                  type: 'button',
                  className: 'feedback-add-image',
                  title: 'Add image',
                  onClick: function(e) { e.preventDefault(); e.stopPropagation(); if (fileInputRef.current) fileInputRef.current.click(); }
                }, h(PlusIcon)) : null
              ),
              h('div', { className: 'feedback-image-hint' }, images.length + ' / ' + MAX_IMAGES + ' images · paste or click +'),
              h('input', {
                ref: fileInputRef,
                type: 'file',
                accept: 'image/*',
                multiple: true,
                style: { display: 'none' },
                onChange: onPickFiles
              })
            ),
            errorMsg || status === 'error'
              ? h('div', { className: 'feedback-error' }, errorMsg || 'Couldn’t send — please try again.')
              : null,
            h('button', {
              type: 'button',
              className: 'feedback-send-btn',
              disabled: !message.trim() || status === 'sending',
              onClick: submit
            }, status === 'sending' ? 'Sending…' : 'Send feedback')
          )
    ), document.body) : null,
    // "Any feedback?" balloon that pops up alongside the shake.
    h('div', {
      className: 'feedback-balloon' + (balloon && !open ? ' feedback-balloon-show' : ''),
      'aria-hidden': 'true'
    }, 'Any feedback? ', h('span', { className: 'feedback-balloon-emoji' }, '😊')),
    h('button', {
      type: 'button',
      className: 'feedback-launcher' + (shaking && !open ? ' feedback-launcher-shake' : '') + (open ? ' feedback-launcher-open' : ''),
      title: 'Send feedback',
      'aria-label': 'Send feedback',
      onClick: function(e) { e.preventDefault(); e.stopPropagation(); setShaking(false); setBalloon(false); setOpen(function(o) { return !o; }); }
    }, h(FeedbackIcon))
  );
}

module.exports = FeedbackWidget;
